/**
 * Official-service RPC client (tycoonex.com).
 * Core (baseUrl resolution, credentials, error shape) mirrors the pioneer-event
 * app's pioneerApi rpc() so both talk to the same web-server the same way.
 * Only Home-facing commands are exposed here — grow this as the MVP grows.
 */

/** Wire compat: web-server RPCs currently expect an eventSlug in the body. */
const EVENT_SLUG = import.meta.env.VITE_EVENT_SLUG?.trim() || "gate-pioneer-2026";

export const TYCOONEX_X_URL = "https://x.com/tycoonex";

export type HomeCollectionEntry = {
  agentId: string;
  ownedDistinct: number;
  ownedTotal: number;
  catalogTotal: number;
};

export type HomeSummary = {
  ip: number;
  rp: number;
  collection: HomeCollectionEntry[];
  /** Rank system not shipped yet — backend sends null */
  rank: unknown | null;
  /** 100-Day IP Race season — null until the season starts */
  season: unknown | null;
  /** True only for users.is_admin — gates the /admin nav entry and route. */
  isAdmin: boolean;
};

export function resolveApiBaseUrl() {
  const configured = import.meta.env.VITE_API_URL?.trim();
  if (configured) return configured.replace(/\/+$/, "");
  if (typeof window !== "undefined") return window.location.origin;
  return "";
}

async function rpc<T>(command: string, params: Record<string, unknown> = {}): Promise<T> {
  const response = await fetch(`${resolveApiBaseUrl()}/rpc/${command}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ eventSlug: EVENT_SLUG, ...params }),
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? `RPC ${command} failed (${response.status})`);
  }

  return response.json() as Promise<T>;
}

/** agent_card_catalog 피니시 티어 — 위에서 아래로 희소해진다 (supply cap 순). */
export type CardFinish =
  | "base"
  | "holo"
  | "blue_ice_sapphire"
  | "gold_ice_sapphire"
  | "red_ice_sapphire"
  | "black_ice_prizms"
  | "padparadscha_sapphire"
  | "gold_vinyl";

export type AgentCardItem = {
  id: string;
  agentId: string;
  rarity: CardFinish;
  owned: boolean;
  count: number;
  supplyCap: number | null;
  mintedCount: number;
  remainingSupply: number | null;
};

export type AgentCardsState = {
  items: AgentCardItem[];
  ownedCount: number;
  totalCount: number;
  dailyFreeAvailable: boolean;
  nextDailyFreeAt: string | null;
};

export type PullGachaResult = {
  cardIds: string[];
  ipBalance: number;
  state: AgentCardsState;
};

export const api = {
  getHomeSummary: () => rpc<HomeSummary>("get_home_summary"),
  listAgentCards: () => rpc<AgentCardsState>("list_agent_cards"),
  pullAgentCardGacha: (
    tier: string,
    count: number,
    agentId?: string,
    packKind?: string,
    /** Fresh crypto.randomUUID() per tap — lets the backend dedupe a dropped-connection retry. */
    idempotencyKey?: string,
  ) =>
    rpc<PullGachaResult>("pull_agent_card_gacha", {
      tier,
      count,
      ...(agentId ? { agentId } : {}),
      ...(packKind ? { packKind } : {}),
      ...(idempotencyKey ? { idempotencyKey } : {}),
    }),
  pullDailyFreeAgentCard: () => rpc<PullGachaResult>("pull_daily_free_agent_card"),
  /**
   * 어시스턴트 채팅 (LlmAuthenticated — 레이트리밋 60/min, 무료 챗 쿼터 차감).
   * strategyId/Label은 백엔드 필수 필드 — 트레이딩 코치 문맥이 아니므로 고정값 사용.
   */
  coachChat: (params: {
    agentId: string;
    agentName: string;
    userMessage: string;
    history?: CoachChatTurn[];
    personaPrompt?: string;
    outputLocale?: string;
  }) =>
    rpc<CoachChatResponse>("assistant_coach_chat", {
      strategyId: "assistant",
      strategyLabel: "Assistant",
      ...params,
    }),
  /**
   * 스타터 어시스턴트 등록 — 계정당 1회(동일 에이전트 재등록은 멱등).
   * 신규 유저의 첫 채팅 시 자동 호출해 프리미엄 게이트를 통과시킨다.
   */
  registerStarterCoach: (agentId: string) =>
    rpc<unknown>("register_starter_coach", { agentId }),
  /**
   * 스타터/프리미엄 상태 조회 — 어느 코치가 스타터로 고정됐는지, 어떤 코치가
   * IP/Premium으로 개별 해금됐는지. Agents 로스터의 Free/IP 배지에 사용.
   */
  getPremiumStatus: () => rpc<PremiumStatus>("get_premium_status"),

  // ── Admin dashboard ──────────────────────────────────────────────────
  adminListUsers: (search?: string, offset?: number) =>
    rpc<AdminUserListResponse>("admin_list_users", {
      ...(search ? { search } : {}),
      ...(offset ? { offset } : {}),
    }),
  adminGetUserDetail: (userId: string) =>
    rpc<AdminUserDetail>("admin_get_user_detail", { userId }),
  adminRemoveUser: (userId: string) =>
    rpc<{ ok: boolean }>("admin_remove_user", { userId }),
  adminDashboardStats: () => rpc<AdminDashboardStats>("admin_dashboard_stats"),

  // ── Marketplace (M2 고정가 리스팅 — docs/MARKETPLACE_PLAN.md) ──────────
  listMarketListings: (query: MarketListingsQuery = {}) =>
    rpc<MarketListingsResponse>("list_market_listings", { ...query }),
  getMarketPriceHistory: (catalogId: string) =>
    rpc<PriceHistoryResponse>("get_market_price_history", { catalogId }),
  getMyCardInstances: () => rpc<{ instances: MyCardInstance[] }>("get_my_card_instances"),
  createMarketListing: (instanceId: number, price: number) =>
    rpc<{ listing: MarketListing }>("create_market_listing", { instanceId, price }),
  cancelMarketListing: (listingId: number) =>
    rpc<{ ok: boolean }>("cancel_market_listing", { listingId }),
  buyMarketListing: (listingId: number) =>
    rpc<BuyResult>("buy_market_listing", { listingId }),
};

// ── Marketplace 타입 (백엔드 marketplace.rs DTO와 1:1) ─────────────────
export type MarketListing = {
  id: number;
  catalogId: string;
  agentId: string;
  finish: CardFinish;
  serial: number | null;
  /** IP 정수 가격 */
  price: number;
  sellerId: string;
  sellerName: string | null;
  createdAt: string;
  /** 요청자가 판매자인지 (비로그인 시 false) */
  mine: boolean;
};

export type MarketSort = "latest" | "price_asc" | "price_desc";

/** list_market_listings 필터/커서 — 전 필드 옵션 (기본: latest, limit 60). */
export type MarketListingsQuery = {
  agentId?: string;
  finish?: string;
  minPrice?: number;
  maxPrice?: number;
  sort?: MarketSort;
  cursorId?: number;
  cursorPrice?: number;
  limit?: number;
};

export type MarketListingsResponse = {
  listings: MarketListing[];
  volume24h: number;
  trades24h: number;
  openListings: number;
  /** keyset 커서 — 다음 페이지가 있을 때만 존재 */
  nextCursorId?: number;
  nextCursorPrice?: number;
};

export type MyCardInstance = {
  instanceId: number;
  catalogId: string;
  agentId: string;
  finish: CardFinish;
  serial: number | null;
  listed: boolean;
  listingId: number | null;
  price: number | null;
};

export type MarketTrade = {
  price: number;
  executedAt: string;
  serial: number | null;
};

export type PriceHistoryResponse = {
  catalogId: string;
  daily: { day: string; close: number; high: number; low: number; volume: number }[];
  recent: MarketTrade[];
};

export type BuyResult = {
  tradeId: number;
  /** 구매 후 내 IP 잔액 */
  ipBalance: number;
};

export type CoachChatTurn = {
  role: "user" | "assistant";
  speaker?: string;
  text: string;
};

export type CoachChatResponse = {
  speaker: string;
  text: string;
  tradeSetup?: unknown;
};

export type PremiumStatus = {
  isPremium: boolean;
  /** The account's one permanent free coach — null until the first-ever send picks it. */
  starterAgentId: string | null;
  /** Coach ids individually unlocked via IP/Premium (starter excluded). */
  unlockedAgents: string[];
};

// ── Admin dashboard (/admin — Admin RPC tier, users.is_admin required) ───
export type AdminUserRow = {
  id: string;
  username: string;
  displayName: string | null;
  ip: number;
  rp: number;
  isPremium: boolean;
  premiumUntil: string | null;
  isAdmin: boolean;
  createdAt: string;
};

export type AdminUserListResponse = {
  users: AdminUserRow[];
  total: number;
  /** Offset cursor for the next page — absent once the list is exhausted. */
  nextOffset?: number;
};

export type AdminWalletEntry = {
  id: number;
  delta: number;
  balanceAfter: number;
  reason: string;
  createdAt: string;
};

export type AdminPaymentRow = {
  id: number;
  status: string;
  amountUsd: number;
  currentPeriodEnd: string | null;
  createdAt: string;
};

export type AdminUserDetail = {
  user: AdminUserRow;
  /** Most recent 50 IP ledger entries. */
  walletLedger: AdminWalletEntry[];
  /** Real-money history — Stripe Premium subscription events (the only paid product today). */
  payments: AdminPaymentRow[];
};

export type AdminDashboardStats = {
  totalUsers: number;
  signupsLast7d: number;
  totalIpCirculating: number;
  totalRpCirculating: number;
  activePremiumCount: number;
  totalPremiumEver: number;
  /** activePremiumCount × the flat Premium price — an estimate, not an invoice total. */
  estimatedMrrUsd: number;
};
